import { createSelector } from "reselect";
import {
  getImageUrlsArray,
  getModifiedLegacyPrices,
  getAddedLegacyPrices,
} from "halifax";
import {
  HotelCfarQuote,
  HotelCancellationPolicyV2,
  PassengerAncillaryPricing,
  AncillaryKindEnum,
  ITrackingProperties,
  ViewedPremierCollectionDetailsProperties,
  RoomInfoProductsWithTransformedIndexes,
  getShopRoomInfoProductsWithTransformedIndexes,
  RoomProduct,
  CorpRoomProduct,
  RoomInfoProducts,
  CorpRoomInfoProducts,
  getNthNightTrackingProperty,
} from "redmond";

import { localCache } from "@capone/common";
import queryStringParser from "query-string";
import { RoomPaymentSchedule } from "redmond/hotels-module/interfaces";
import { IStoreState } from "../../../../reducers/types";
import { PremierCollectionShopCallState } from "../index";
import { getSelectedAccount } from "../../../rewards/reducer";
import {
  getIsFromHotelAvailability,
  getViewedPremierCollectionListProperties,
  getSearchedNightCount,
} from "../../../availability/reducer";
import {
  lodgingIdWithCfarQuotesSelector,
  getHotelCfarQuoteByIndexSelector,
  getHotelCancellationPolicyByIndexSelector,
  getHotelAdditionalInfoByIndexSelector,
  isHotelCfarEnabledSelector,
  selectedCfarIdSelector,
  hasSelectedRefundableRoomSelector,
} from "../../../ancillary/reducer/selectors";
import { getTravelWalletCredit } from "../../../travel-wallet/reducer";
import { getIsFromHotelDatelessSearch } from "../../../search/reducer";
import {
  hasPayLaterProducts,
  isAllowedPayLaterLodging,
} from "../../utils/payLater";

export const getPremierCollectionShopSelectedAvailability = (
  state: IStoreState
) => state.premierCollectionShop.selectedLodging;

export const getPremierCollectionShopImageUrlsArray = createSelector(
  [getPremierCollectionShopSelectedAvailability],
  (availability) => getImageUrlsArray(availability?.lodging.media ?? [])
);

export const getPremierCollectionShopSelectedLodgingId = createSelector(
  getPremierCollectionShopSelectedAvailability,
  (selectedLodging) => selectedLodging?.lodging.id
);

export const getCacheLodgingTokenResponse = (state: IStoreState) =>
  state.premierCollectionShop.cacheLodgingTokenResponse;

export const getPremierCollectionShopShopRequestId = createSelector(
  [getPremierCollectionShopSelectedAvailability, getCacheLodgingTokenResponse],
  (availability, cacheLodgingTokenResponse) => {
    const { shopToken } = queryStringParser.parse(window.location.search);

    const localCacheShopRequest = localCache.get(shopToken as string);

    return cacheLodgingTokenResponse
      ? cacheLodgingTokenResponse.opaqueShopRequest
      : localCacheShopRequest || availability?.price?.opaqueShopRequest;
  }
);

export const getPremierCollectionShopCancellationSummary = (
  state: IStoreState
) => state.premierCollectionShop.cancellationSummary;

export const getPremierCollectionShopRoomInfoProducts = (state: IStoreState) =>
  state.premierCollectionShop.roomInfoProducts;

export const getPremierCollectionShopChosenRoomInfoIndex = (
  state: IStoreState
) => state.premierCollectionShop.chosenRoomInfoIndex;

export const getPremierCollectionShopChosenProductIndex = (
  state: IStoreState
) => state.premierCollectionShop.chosenProductIndex;

export const getAllowedPayLaterLodgingIds = (state: IStoreState) =>
  state.premierCollectionShop.allowedPayLaterLodgingIds;

export const getIsFromPremiumOffers = (state: IStoreState) =>
  state.premierCollectionShop.isFromPremiumOffers;

export const getPremierCollectionShopChosenRoomInfo = createSelector(
  [
    getPremierCollectionShopRoomInfoProducts,
    getPremierCollectionShopChosenRoomInfoIndex,
  ],
  (
    roomInfoProducts,
    chosenRoomInfoIndex
  ): RoomInfoProducts | CorpRoomInfoProducts | null => {
    if (chosenRoomInfoIndex !== null) {
      return roomInfoProducts[chosenRoomInfoIndex] ?? null;
    }

    return null;
  }
);

export const getPremierCollectionShopChosenProduct = createSelector(
  [
    getPremierCollectionShopRoomInfoProducts,
    getPremierCollectionShopChosenRoomInfoIndex,
    getPremierCollectionShopChosenProductIndex,
  ],
  (
    roomInfoProducts,
    chosenRoomInfoIndex,
    chosenProductIndex
  ): RoomProduct | CorpRoomProduct | null => {
    if (chosenRoomInfoIndex !== null && chosenProductIndex !== null) {
      return (
        roomInfoProducts[chosenRoomInfoIndex]?.products[chosenProductIndex] ??
        null
      );
    }

    return null;
  }
);

export const getIsChosenRoomPayLater = createSelector(
  [
    getPremierCollectionShopChosenProduct,
    getAllowedPayLaterLodgingIds,
    getPremierCollectionShopSelectedLodgingId,
  ],
  (product, allowedLodgingIds, lodgingId) =>
    product?.roomPaymentSchedule === RoomPaymentSchedule.PayLater &&
    isAllowedPayLaterLodging(allowedLodgingIds, lodgingId)
);

export const getChosenRoomHasPayLater = createSelector(
  [
    getPremierCollectionShopChosenRoomInfo,
    getAllowedPayLaterLodgingIds,
    getPremierCollectionShopSelectedLodgingId,
  ],
  (chosenRoomInfo, allowedLodgingIds, lodgingId) =>
    isAllowedPayLaterLodging(allowedLodgingIds, lodgingId) &&
    hasPayLaterProducts(chosenRoomInfo?.products)
);

export const getPremierCollectionShopCallState = (state: IStoreState) =>
  state.premierCollectionShop.premierCollectionShopCallState;

export const getPremierCollectionShopCallError = (state: IStoreState) =>
  state.premierCollectionShop.premierCollectionShopCallError;

export const getCheapestProduct = createSelector(
  getPremierCollectionShopRoomInfoProducts,
  (roomInfoProducts): RoomProduct | CorpRoomProduct | null => {
    const firstRoom = roomInfoProducts[0];
    if (!firstRoom) return null;
    return (
      firstRoom.products.find(
        (p) => p?.roomPaymentSchedule === RoomPaymentSchedule.PayNow
      ) ??
      firstRoom.products[0] ??
      null
    );
  }
);

export const isMissingProductInfo = createSelector(
  getPremierCollectionShopCallState,
  getPremierCollectionShopRoomInfoProducts,
  (premierCollectionShopCallState, roomInfoProducts) =>
    premierCollectionShopCallState === PremierCollectionShopCallState.Success &&
    roomInfoProducts.length === 0
);

export const isMissingPayNowRates = createSelector(
  getPremierCollectionShopCallState,
  getPremierCollectionShopRoomInfoProducts,
  (premierCollectionShopCallState, roomInfoProducts) =>
    premierCollectionShopCallState === PremierCollectionShopCallState.Success &&
    !roomInfoProducts.some((roomInfo) =>
      roomInfo.products.some(
        (product) => product?.roomPaymentSchedule === RoomPaymentSchedule.PayNow
      )
    )
);

export const hasPremierCollectionShopFailed = createSelector(
  getPremierCollectionShopCallState,
  isMissingProductInfo,
  isMissingPayNowRates,
  (premierCollectionShopCallState, missingProductInfo, missingPayNowRates) =>
    premierCollectionShopCallState === PremierCollectionShopCallState.Failed ||
    missingProductInfo ||
    missingPayNowRates
);

export const getViewedPremierCollectionDetailsTrackingProps = createSelector(
  getPremierCollectionShopChosenProduct,
  getPremierCollectionShopChosenRoomInfo,
  (product, roomInfo): ITrackingProperties | null =>
    product && roomInfo
      ? {
          properties: {
            ...product?.trackingPropertiesV2?.properties,
            ...roomInfo?.trackingPropertiesV2?.properties,
          },
          encryptedProperties: [
            product?.trackingPropertiesV2?.encryptedProperties ?? "",
            roomInfo?.trackingPropertiesV2?.encryptedProperties ?? "",
          ],
        }
      : null
);

export const getPCHotelShopTrackingPropertiesV2 = (state: IStoreState) =>
  state.premierCollectionShop.hotelShopTrackingPropertiesV2;

const getHotelShopRoomInfoRoomsImagesCount = createSelector(
  getPremierCollectionShopRoomInfoProducts,
  (
    roomInfoProducts
  ): {
    rooms_with_images_count: number;
    rooms_without_images_count: number;
  } => {
    let rooms_with_images_cnt = 0;
    let rooms_without_images_cnt = 0;
    for (const roomInfoProduct of roomInfoProducts) {
      rooms_with_images_cnt += roomInfoProduct.roomInfo.media.length ? 1 : 0;
      rooms_without_images_cnt += roomInfoProduct.roomInfo.media.length ? 0 : 1;
    }
    return {
      rooms_with_images_count: rooms_with_images_cnt,
      rooms_without_images_count: rooms_without_images_cnt,
    };
  }
);

export const getSelectedLodgingIsFavorited = (state: IStoreState) =>
  state.premierCollectionShop.selectedLodgingIsFavorited;

export const getHotelDetailsEntrySource = (state: IStoreState) =>
  state.premierCollectionShop.hotelDetailsEntrySource;

export const getPersistentLodgingTrackingProperties = (state: IStoreState) =>
  state.premierCollectionShop.persistentLodgingTrackingProperties;

export const getViewedPremierCollectionDetailsProperties = createSelector(
  getViewedPremierCollectionListProperties,
  getSelectedAccount,
  getPremierCollectionShopSelectedAvailability,
  getViewedPremierCollectionDetailsTrackingProps,
  getPremierCollectionShopRoomInfoProducts,
  getIsFromHotelAvailability,
  getPCHotelShopTrackingPropertiesV2,
  getHotelShopRoomInfoRoomsImagesCount,
  getIsFromHotelDatelessSearch,
  getIsFromPremiumOffers,
  getPersistentLodgingTrackingProperties,
  getSelectedLodgingIsFavorited,
  getHotelDetailsEntrySource,
  (
    viewedPremierCollectionListProperties,
    account,
    availability,
    trackingProps,
    roomInfoProducts,
    isFromHotelFunnel,
    pcShopTrackingProperties,
    roomInfoRoomsImagesCount,
    isFromHotelDatelessSearch,
    isFromPremiumOffers,
    persistentLodgingTrackingProperties,
    selectedLodgingIsFavorited,
    hotelDetailsEntrySource
  ): ITrackingProperties<ViewedPremierCollectionDetailsProperties> => {
    const paymentSchedulesAvailable = [
      ...new Set(
        roomInfoProducts.flatMap((roomInfo) =>
          roomInfo.products.map((product) => product?.roomPaymentSchedule)
        )
      ),
    ]
      .sort()
      .join();

    const roomsWithNoPayNowRates = roomInfoProducts.filter(
      (roomInfo) =>
        !roomInfo.products.some(
          (product) =>
            product?.roomPaymentSchedule === RoomPaymentSchedule.PayNow
        )
    );

    return {
      properties: {
        ...viewedPremierCollectionListProperties.properties,
        account_type_selected: account?.productDisplayName || "",
        ...availability?.trackingPropertiesV2?.properties,
        rooms_shown: roomInfoProducts.length,
        ...availability?.bestOfferThisLodging?.trackingPropertiesV2?.properties,
        has_offer: !!availability?.bestOfferThisLodging,
        account_use_type: account?.accountUseType,
        account_allow_rewards_redemption: account?.allowRewardsRedemption,
        ...trackingProps?.properties,
        funnel: isFromHotelFunnel ? "hotel" : "premium_stays",
        ...pcShopTrackingProperties?.properties,
        rooms_with_images_count:
          roomInfoRoomsImagesCount.rooms_with_images_count,
        rooms_without_images_count:
          roomInfoRoomsImagesCount.rooms_without_images_count,
        nth_night_promo: getNthNightTrackingProperty(availability),
        is_dateless: isFromHotelDatelessSearch ?? false,
        is_from_premium_offers_page: isFromPremiumOffers ?? false,
        payment_schedule_available: paymentSchedulesAvailable,
        rooms_with_no_pay_now_rates_count: roomsWithNoPayNowRates.length,
        is_favorited: selectedLodgingIsFavorited ?? false,
        hotel_details_entry_source: hotelDetailsEntrySource || undefined,
        ...persistentLodgingTrackingProperties,
      },
      encryptedProperties: [
        ...viewedPremierCollectionListProperties.encryptedProperties,
        availability?.trackingPropertiesV2?.encryptedProperties ?? "",
        availability?.bestOfferThisLodging?.trackingPropertiesV2
          ?.encryptedProperties ?? "",
        ...(trackingProps?.encryptedProperties ?? []),
        pcShopTrackingProperties?.encryptedProperties ?? "",
      ],
    };
  }
);

export const premierCollectionShopProgressSelector = (state: IStoreState) =>
  state.premierCollectionShop.progress;

// Travel Wallet
export const showOfferBasedOnSelectedLodgingSelector = createSelector(
  getPremierCollectionShopSelectedAvailability,
  getTravelWalletCredit,
  (selectedLodging, credit): boolean =>
    !!selectedLodging?.bestOfferThisLodging &&
    ((!!credit &&
      Math.abs(credit.amount.amount) ===
        Math.abs(selectedLodging.bestOfferThisLodging.amount.amount)) ||
      !!selectedLodging.bestOfferThisLodging.availabilityPageBanner)
);

// CFAR
export const hotelCfarQuoteFromChosenRoomProductSelector = createSelector(
  getPremierCollectionShopSelectedLodgingId,
  lodgingIdWithCfarQuotesSelector,
  getPremierCollectionShopChosenRoomInfoIndex,
  getPremierCollectionShopChosenProductIndex,
  getHotelCfarQuoteByIndexSelector,
  (
    hotelShopSelectedLodgingId,
    lodgingIdWithCfarQuotes,
    hotelShopChosenRoomInfoIndex,
    hotelShopChosenProductIndex,
    getHotelCfarQuoteByIndex
  ): HotelCfarQuote | null => {
    if (
      hotelShopChosenRoomInfoIndex === null ||
      hotelShopChosenProductIndex === null ||
      !hotelShopSelectedLodgingId ||
      !lodgingIdWithCfarQuotes ||
      hotelShopSelectedLodgingId !== lodgingIdWithCfarQuotes
    ) {
      return null;
    }

    return getHotelCfarQuoteByIndex({
      roomInfoIndex: hotelShopChosenRoomInfoIndex,
      productIndex: hotelShopChosenProductIndex,
    });
  }
);

export const cancellationPolicyFromChosenRoomProductSelector = createSelector(
  getPremierCollectionShopSelectedLodgingId,
  lodgingIdWithCfarQuotesSelector,
  getPremierCollectionShopChosenRoomInfoIndex,
  getPremierCollectionShopChosenProductIndex,
  getHotelCancellationPolicyByIndexSelector,
  (
    hotelShopSelectedLodgingId,
    lodgingIdWithCfarQuotes,
    hotelShopChosenRoomInfoIndex,
    hotelShopChosenProductIndex,
    getHotelCancellationPolicyByIndex
  ): HotelCancellationPolicyV2 | null => {
    if (
      hotelShopChosenRoomInfoIndex === null ||
      hotelShopChosenProductIndex === null ||
      !hotelShopSelectedLodgingId ||
      !lodgingIdWithCfarQuotes ||
      hotelShopSelectedLodgingId !== lodgingIdWithCfarQuotes
    ) {
      return null;
    }

    return getHotelCancellationPolicyByIndex({
      roomInfoIndex: hotelShopChosenRoomInfoIndex,
      productIndex: hotelShopChosenProductIndex,
    });
  }
);

export const getAddedAncillariesPricing = createSelector(
  selectedCfarIdSelector,
  hotelCfarQuoteFromChosenRoomProductSelector,
  (
    selectedCfarId,
    hotelCfarQuoteFromChosenRoomProduct
  ): PassengerAncillaryPricing[] => {
    const ancillaryPricing: PassengerAncillaryPricing[] = [];
    if (
      !!selectedCfarId &&
      !!hotelCfarQuoteFromChosenRoomProduct &&
      selectedCfarId.value === hotelCfarQuoteFromChosenRoomProduct.id.value
    ) {
      ancillaryPricing.push({
        kind: AncillaryKindEnum.Cfar,
        premium: hotelCfarQuoteFromChosenRoomProduct.premiumPrices,
      });
    }
    return ancillaryPricing;
  }
);

export const isCfarAvailableInChosenRoomProductSelector = createSelector(
  isHotelCfarEnabledSelector,
  getPremierCollectionShopChosenRoomInfoIndex,
  getPremierCollectionShopChosenProductIndex,
  hotelCfarQuoteFromChosenRoomProductSelector,
  hasSelectedRefundableRoomSelector,
  (
    isHotelCfarEnabled,
    hotelShopChosenRoomInfoIndex,
    hotelShopChosenProductIndex,
    hotelCfarQuoteFromChosenRoomProduct,
    hasSelectedRefundableRoom
  ): boolean =>
    isHotelCfarEnabled &&
    hotelShopChosenRoomInfoIndex !== null &&
    hotelShopChosenProductIndex !== null &&
    hotelCfarQuoteFromChosenRoomProduct !== null &&
    !hasSelectedRefundableRoom
);

export const hasSelectedCfarOptionSelector = createSelector(
  selectedCfarIdSelector,
  isCfarAvailableInChosenRoomProductSelector,
  (selectedCfarId, isCfarAvailable): boolean =>
    !isCfarAvailable || !!selectedCfarId
);

export const isCfarOptionSelectionCompleteSelector = createSelector(
  isCfarAvailableInChosenRoomProductSelector,
  hasSelectedCfarOptionSelector,
  isHotelCfarEnabledSelector,
  (isCfarAvailable, hasSelectedCfarOption, isCfarEnabled): boolean =>
    !isCfarEnabled || !isCfarAvailable || hasSelectedCfarOption
);

export const isAddOnOptionAvailableSelector = createSelector(
  isCfarAvailableInChosenRoomProductSelector,
  (isCfarAvailable): boolean => isCfarAvailable
);

export const isOptionSelectionCompleteSelector = createSelector(
  isCfarOptionSelectionCompleteSelector,
  (isCfarOptionSelectionComplete): boolean => isCfarOptionSelectionComplete
);

export const getPremierCollectionShopRoomInfoProductsWithTransformedIndexes =
  createSelector(
    getPremierCollectionShopRoomInfoProducts,
    getHotelCfarQuoteByIndexSelector,
    getHotelCancellationPolicyByIndexSelector,
    getHotelAdditionalInfoByIndexSelector,
    getSearchedNightCount,
    getPremierCollectionShopSelectedAvailability,
    (
      roomInfoProducts,
      getHotelCfarQuoteByIndex,
      getHotelCancellationPolicyByIndex,
      getHotelAdditionalInfoByIndex,
      nightCount,
      selectedAvailability
    ): RoomInfoProductsWithTransformedIndexes[] =>
      getShopRoomInfoProductsWithTransformedIndexes({
        roomInfoProducts,
        getHotelCfarQuoteByIndex,
        getHotelCancellationPolicyByIndex,
        getHotelAdditionalInfoByIndex,
        nightCount,
        pricesHelpers: {
          getModifiedLegacyPrices,
          getAddedLegacyPrices,
        },
        displayTaxesAndFeesIncluded:
          selectedAvailability?.price?.displayTaxesAndFeesIncluded,
        displayTaxesAndFeesIncludedNightlyPrice:
          selectedAvailability?.price?.displayTaxesAndFeesIncludedNightlyPrice,
      })
  );

export const getPremierCollectionShopChosenProductHotelLoyaltyProgramCode = createSelector(
  [getPremierCollectionShopChosenProduct],
  (product) =>
    product && "loyaltyProgramCode" in product
      ? product.loyaltyProgramCode ?? ""
      : ""
);

export const getIsLifestyleEligible = (state: IStoreState) =>
  state.premierCollectionShop.isLifestyleEligible;